// "Tạo mới" ở ô Bảng giá / Bảng chiết khấu khi thêm cấp dưới (docs/KE_HOACH_BANG_GIA_KHI_TAO_DON_VI.md §4.2): mở đúng
// hộp lập bảng của trang Bảng giá / Chiết khấu, đè lên form — chữ đã nhập trong form giữ nguyên. Lưu xong bảng vào
// thư viện và form tự chọn bảng đó. Nạp như LibrarySection (routes/PriceLists.tsx): giá nhập để so (thấp hơn chỉ cảnh báo),
// thư viện để chép giá.

import { useMemo } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { get } from '@/lib/api'
import { normalizePriceListData, type PriceGrid } from '@/components/PriceListEditor'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { PriceListFormDialog, type CopySource } from './PriceListFormDialog'
import type { LibraryKind, LibraryRow, MineResponse, SaveResponse } from './types'
import { SchemeFormDialog } from '@/components/commissions/SchemeFormDialog'
import { currentMonthVN } from '@/components/commissions/types'

export interface CreatedTable {
  id: string
  code: string
  name: string
}

export function NewPriceListDialog({ kind, onClose, onCreated }: { kind: LibraryKind; onClose: () => void; onCreated: (t: CreatedTable) => void }) {
  const qc = useQueryClient()
  const mineQ = useQuery({ queryKey: ['price-lists', 'mine'], queryFn: () => get<MineResponse>('/price-lists/mine') })
  const gridQ = useQuery({ queryKey: ['price-lists', 'grid'], queryFn: () => get<{ grid: PriceGrid | null }>('/price-lists/grid') })
  const libQ = useQuery({ queryKey: ['price-lists', 'library'], queryFn: () => get<{ priceLists: LibraryRow[] }>('/price-lists/library') })

  const grid = gridQ.data?.grid ?? null
  const cur = mineQ.data?.mine?.current ?? null
  const buyGrid = cur?.grid ?? null
  const buyData = cur && buyGrid ? normalizePriceListData(buyGrid, cur.data) : null
  const library = libQ.data?.priceLists ?? []
  const copySources: CopySource[] = useMemo(
    () => [
      ...(buyData && buyGrid ? [{ id: '__buy__', label: 'Giá nhập của tôi', data: buyData, grid: buyGrid }] : []),
      ...library.flatMap((l) => {
        const g = l.grid ?? grid ?? buyGrid
        return g ? [{ id: l.id, label: `${l.name} (${l.code})`, data: l.data, grid: g }] : []
      }),
    ],
    [buyData, buyGrid, library, grid],
  )

  if (mineQ.isLoading || gridQ.isLoading || libQ.isLoading) return null
  const rows = buyGrid ?? grid
  if (!buyData || !rows) {
    // Đơn vị chưa có giá nhập thì không lập được bảng cho cấp dưới (P4) — báo và đóng
    return (
      <Dialog open onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Chưa lập được bảng giá</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-ink">Bạn chưa được giao bảng giá nhập.</p>
          <DialogFooter>
            <Button type="button" onClick={onClose}>Đóng</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  function onSaved(res: SaveResponse) {
    toast.success(`Đã tạo bảng ${res.priceList.code}`)
    qc.invalidateQueries({ queryKey: ['price-lists'] })
    qc.invalidateQueries({ queryKey: ['network-price-lists'] })
    onCreated({ id: res.priceList.id, code: res.priceList.code, name: res.priceList.name })
  }

  return (
    <PriceListFormDialog grid={rows} kind={kind} list={null} buyData={buyData} buyGrid={buyGrid} copySources={copySources} onClose={onClose} onSaved={onSaved} />
  )
}

export function NewSchemeDialog({ onClose, onCreated }: { onClose: () => void; onCreated: (t: CreatedTable) => void }) {
  const qc = useQueryClient()
  return (
    <SchemeFormDialog
      scheme={null}
      inUse={0}
      month={currentMonthVN()}
      onClose={onClose}
      onSaved={(r) => {
        if (r.kind !== 'created') return
        toast.success(`Đã tạo bảng ${r.code}`)
        qc.invalidateQueries({ queryKey: ['commissions'] })
        onCreated({ id: r.id, code: r.code, name: r.name })
      }}
    />
  )
}
